import { api } from '../api/client';
import { Product, StockBalance, StockOut, StockMovement, StockReturnRecord } from '../types';
import { toDate, toMillis } from '../utils/dates';

type StockOutInfo = {
  usedForOut: boolean;
  soldDate: string | Date | null;
  record: (StockOut & { docId: string }) | null;
  invoiceNumber: string | null;
  invoiceDate: string | Date | null;
  warrantyNumber: string | null;
};

export const getStockBalance = async (barcode: string): Promise<StockBalance> => {
  return api.get<StockBalance>(`/stock/balance/${encodeURIComponent(barcode)}`);
};

export const getStockBalances = async (barcodes: string[]): Promise<Map<string, StockBalance>> => {
  if (barcodes.length === 0) return new Map();
  const data = await api.post<Record<string, StockBalance>>('/stock/balances', { barcodes });
  return new Map(Object.entries(data));
};

export const isBarcodeUsedForStockIn = async (barcode: string): Promise<boolean> => {
  const bal = await getStockBalance(barcode);
  return bal.totalIn > 0;
};

export const isBarcodeUsedForStockOut = async (barcode: string): Promise<boolean> => {
  const info = await getStockOutInfoForBarcode(barcode);
  return info.usedForOut;
};

export const getStockOutInfoForBarcode = async (barcode: string): Promise<{
  usedForOut: boolean;
  soldDate: Date | null;
  record: (StockOut & { docId: string }) | null;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  warrantyNumber: string | null;
}> => {
  const info = await api.get<StockOutInfo>(`/stock/out-info/${encodeURIComponent(barcode)}`);
  return {
    usedForOut: info.usedForOut,
    soldDate: toDate(info.soldDate),
    record: info.record,
    invoiceNumber: info.invoiceNumber ?? null,
    invoiceDate: toDate(info.invoiceDate),
    warrantyNumber: info.warrantyNumber ?? null
  };
};

export const getSoldDateForBarcode = async (barcode: string): Promise<Date | null> => {
  const info = await getStockOutInfoForBarcode(barcode);
  return info.soldDate;
};

export const addStockIn = async (
  barcode: string,
  quantity: number,
  _userId: string,
  _userName: string,
  notes?: string,
  date?: Date,
  itemCondition?: 'new' | 'damaged' | 'returned',
  damageReason?: 'transport' | 'manufacturing' | 'storage' | 'customer_return'
): Promise<void> => {
  await api.post('/stock/in', {
    barcode,
    quantity,
    notes,
    ...(date && { date: date.toISOString() }),
    ...(itemCondition && { itemCondition }),
    ...(damageReason && { damageReason })
  });
};

export const addStockOut = async (
  barcode: string,
  quantity: number,
  _userId: string,
  _userName: string,
  reason: string,
  notes?: string,
  date?: Date
): Promise<void> => {
  await api.post('/stock/out', {
    barcode,
    quantity,
    reason,
    notes,
    ...(date && { date: date.toISOString() })
  });
};

export const getStockMovements = async (
  startDate?: Date,
  endDate?: Date,
  barcode?: string,
  limitCount?: number
): Promise<StockMovement[]> => {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate.toISOString());
  if (endDate) params.set('endDate', endDate.toISOString());
  if (barcode) params.set('barcode', barcode);
  if (limitCount != null) params.set('limit', String(limitCount));
  const qs = params.toString();
  const movements = await api.get<StockMovement[]>(`/stock/movements${qs ? `?${qs}` : ''}`);
  return movements.sort((a, b) => toMillis(b.date) - toMillis(a.date));
};

export const getRecentMovements = async (limitCount: number = 10): Promise<StockMovement[]> => {
  return getStockMovements(undefined, undefined, undefined, limitCount);
};

export const getTodayStockTotals = async (): Promise<{ todayIn: number; todayOut: number }> => {
  return api.get<{ todayIn: number; todayOut: number }>('/stock/today-totals');
};

export const getLowStockProducts = async (): Promise<Array<Product & { balance: number }>> => {
  const { getAllProducts } = await import('./productService');
  const products = await getAllProducts();
  const balances = await getStockBalances(products.map((p) => p.barcode));
  const lowStockProducts: Array<Product & { balance: number }> = [];
  for (const product of products) {
    const bal = balances.get(product.barcode);
    const balanceVal = bal?.balance ?? 0;
    if (balanceVal < product.lowStockThreshold) {
      lowStockProducts.push({ ...product, balance: balanceVal });
    }
  }
  return lowStockProducts;
};

export const getStockOutRecordForBarcode = async (barcode: string): Promise<(StockOut & { docId: string }) | null> => {
  const info = await getStockOutInfoForBarcode(barcode);
  return info.record;
};

export const addStockReturn = async (
  barcode: string,
  productName: string,
  quantity: number,
  _userId: string,
  _userName: string,
  reason: StockReturnRecord['reason'],
  stockOutDocId: string,
  notes?: string
): Promise<void> => {
  await api.post('/stock/return', {
    barcode,
    productName,
    quantity,
    reason,
    notes,
    stockOutId: stockOutDocId
  });
};

export const getStockReturns = async (limitCount: number = 50): Promise<StockReturnRecord[]> => {
  return api.get<StockReturnRecord[]>(`/stock/returns?limit=${limitCount}`);
};

// Legacy exports kept for callers that checked stock-in duplicate before submit
export const getTotalStockIn = async (barcode: string): Promise<number> => {
  const bal = await getStockBalance(barcode);
  return bal.totalIn;
};

export const getTotalStockOut = async (barcode: string): Promise<number> => {
  const bal = await getStockBalance(barcode);
  return bal.totalOut;
};

